import type { DefaultColors } from "tailwindcss/types/generated/colors";

import twColors from "tailwindcss/colors";

// Remove deprecated Tailwind color aliases to avoid warnings noise in console
type Colors = Omit<
  DefaultColors,
  "blueGray" | "coolGray" | "lightBlue" | "trueGray" | "warmGray"
>;

const alpha = (hex: string, alpha: number): string => {
  let color = hex.replace(/^#/, "");
  if (color.length === 3) {
    color = color
      .split("")
      .map((c) => c + c)
      .join("");
  }

  let a = alpha;
  if (a <= 1) {
    a = Math.round(255 * a);
  }
  if (a < 0) a = 0;
  if (a > 255) a = 255;
  const alphaHex = a.toString(16).padStart(2, "0").toLowerCase();

  return `#${color}${alphaHex}`;
};

// https://github.com/tailwindlabs/tailwindcss/discussions/11127#discussioncomment-10919929
const colors = Object.fromEntries(
  Object.entries(Object.getOwnPropertyDescriptors(twColors))
    .filter(([, d]) => typeof d.get !== "function")
    .map(([k, d]) => [k, d.value]),
) as Colors;

export const dsColors = {
  ...colors,
  blue: {
    50: "#eff6ff",
    75: "#e8f0ff",
    100: "#dbeafe",
    200: "#cad9ff",
    300: "#99c5f7",
    400: "#60a5fa",
    500: "#3b82f6",
    600: "#2a64f6",
    700: "#1d4ed8",
    800: "#1e40af",
    900: "#1e3a8a",
    950: "#172554",
  },
  red: {
    50: "#fef2f2",
    100: "#ffe8e2",
    200: "#ffd2c8",
    300: "#ffb6a6",
    400: "#fd846a",
    500: "#ed6446",
    600: "#dc4726",
    700: "#c03e21",
    800: "#a3371d",
    900: "#832e1a",
    950: "#4c170b",
  },
  zinc: {
    ...twColors.zinc,
    300: "#cfd8ed",
  },
  // Row-highlight tints for data tables (green / red / yellow).
  highlight: {
    success: "#cdf6da",
    error: "#ffe1e1",
    warning: "#fff8da",
  },
} as const;

export const dsThemeColors = {
  accent: {
    DEFAULT: dsColors.zinc[100],
    foreground: dsColors.zinc[900],
  },
  background: dsColors.white,
  border: dsColors.zinc[300],
  card: {
    DEFAULT: dsColors.white,
    foreground: dsColors.zinc[950],
  },
  destructive: {
    DEFAULT: dsColors.red[600],
    foreground: dsColors.red[50],
  },
  foreground: dsColors.zinc[900],
  input: dsColors.zinc[200],
  muted: {
    DEFAULT: dsColors.zinc[100],
    foreground: dsColors.zinc[500],
  },
  popover: {
    DEFAULT: dsColors.white,
    foreground: dsColors.zinc[950],
  },
  primary: {
    DEFAULT: dsColors.blue[600],
    foreground: dsColors.zinc[50],
  },
  ring: dsColors.blue[900],
  secondary: {
    DEFAULT: dsColors.blue[200],
    foreground: dsColors.zinc[900],
  },
  interface: {
    DEFAULT: dsColors.zinc[50],
    foreground: dsColors.zinc[700],
    background: dsColors.white,
    primary: {
      DEFAULT: dsColors.zinc[900],
      foreground: dsColors.zinc[50],
    },
    accent: {
      DEFAULT: dsColors.blue[100],
      light: dsColors.blue[75],
      foreground: dsColors.zinc[900],
    },
    border: dsColors.gray[200],
    ring: dsColors.zinc[400],
  },
  success: {
    DEFAULT: dsColors.green[50],
    // The lightest green, for filling a surface that reads as "this held up".
    // Pairs with `destructive-foreground`, which is the lightest red.
    background: dsColors.green[50],
    foreground: dsColors.green[800],
    accent: dsColors.green[500],
  },
  warning: dsColors.amber[600],
  attention: {
    DEFAULT: dsColors.yellow[50],
    foreground: dsColors.yellow[700],
  },
  active: {
    DEFAULT: dsColors.blue[50],
    foreground: dsColors.blue[700],
  },
  inactive: {
    DEFAULT: dsColors.zinc[50],
    foreground: dsColors.zinc[500],
  },
  informational: {
    DEFAULT: dsColors.zinc[50],
    foreground: dsColors.zinc[700],
    accent: alpha(dsColors.zinc[50], 0.3),
  },
  // Semantic row-highlight backgrounds, e.g. af-bg-highlight-success.
  // Consumers map their own domain states onto these (see claimLineTableStyleProvider).
  highlight: {
    success: dsColors.highlight.success,
    error: dsColors.highlight.error,
    warning: dsColors.highlight.warning,
  },
};
